// src/components/profile/ProfileManager.jsx - Complete revision
import React, { useState, useEffect } from 'react';
import { 
  Plus, X, Search, Filter, Upload, User, Download, Grid, List, Edit, 
  Trash2, Copy, Eye, EyeOff, Move, Settings, Calendar, Award, Briefcase,
  Building, Package, MessageCircle, Star, Mail, Phone, Globe, MapPin,
  Trophy, CheckCircle, Image as ImageIcon, BarChart3, Users, ArrowUp, ArrowDown,
  CheckLineIcon
} from 'lucide-react';
import { profileService } from '../../services/profileService';
import { ProfileSectionForm } from './ProfileSectionForm';
import { ProfileSectionCard } from './ProfileSectionCard';
import { ProfileSectionPreview } from './ProfileSectionPreview';

export const ProfileManager = ({ bookId, book, user, onUpdate }) => {
  const [sections, setSections] = useState([]);
  const [filteredSections, setFilteredSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewSection, setPreviewSection] = useState(null);
  const [draggedSection, setDraggedSection] = useState(null);
  const [sectionStats, setSectionStats] = useState({});
  const [bulkSelectedSections, setBulkSelectedSections] = useState(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);

  const [filters, setFilters] = useState({
    type: 'all',
    status: 'all',
    visibility: 'all'
  });

  const profileType = book?.subCategory || 'personal';
  const profileSettings = book?.profileSettings || {};

  useEffect(() => {
    loadSections();
    loadSectionStats();
  }, [bookId]);

  useEffect(() => {
    applyFiltersAndSearch();
  }, [sections, searchTerm, filters]);

  const loadSections = async () => {
    try {
      const sectionsData = await profileService.getProfileSections(bookId);
      setSections(sectionsData);
    } catch (error) {
      console.error('Error loading profile sections:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSectionStats = async () => {
    try {
      const stats = await profileService.getProfileSectionStats(bookId);
      setSectionStats(stats);

      const enhancedStats = {
      ...stats,
        references: {
          total: sections.filter(s => s.type === 'references').length,
          items: sections
            .filter(s => s.type === 'references')
            .reduce((acc, section) => acc + (section.items?.length || 0), 0),
          contactable: sections
            .filter(s => s.type === 'references')
            .reduce((acc, section) => 
              acc + (section.items?.filter(item => item.canContact !== false).length || 0), 0
            )
        }
      };
    } catch (error) {
      console.error('Error loading section stats:', error);
    }
  };

  const applyFiltersAndSearch = () => {
    let filtered = sections;

    // Apply search
    if (searchTerm) {
      filtered = filtered.filter(section =>
        section.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        section.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        section.content?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply filters
    if (filters.type !== 'all') {
      filtered = filtered.filter(section => section.type === filters.type);
    }

    if (filters.status !== 'all') {
      filtered = filtered.filter(section => section.status === filters.status);
    }

    if (filters.visibility !== 'all') {
      const isVisible = filters.visibility === 'visible';
      filtered = filtered.filter(section => 
        profileSettings.sectionVisibility?.[section.type] === isVisible
      );
    }

    // Sort by order
    filtered.sort((a, b) => (a.order || 0) - (b.order || 0));
    
    setFilteredSections(filtered);
  };

  const handleSaveSection = async (sectionData) => {
    try {
      // Check for BOTH editingSection AND editingSection.id to determine update vs create
      if (editingSection && editingSection.id) {
        // Update existing section
        await profileService.updateProfileSection(bookId, editingSection.id, sectionData);
      } else {
        // Create new section
        await profileService.addProfileSection(bookId, {
          ...sectionData,
          order: sections.length
        });
      }
      
      await loadSections();
      await loadSectionStats();
      setShowForm(false);
      setEditingSection(null);
    } catch (error) {
      console.error('Error saving section:', error);
      alert('Failed to save section');
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (window.confirm('Are you sure you want to delete this section?')) {
      try {
        await profileService.deleteProfileSection(bookId, sectionId);
        await loadSections();
        await loadSectionStats();
      } catch (error) {
        console.error('Error deleting section:', error);
        alert('Failed to delete section');
      }
    }
  };

  const handleDuplicateSection = async (sectionId) => {
    try {
      const section = await profileService.getProfileSection(bookId, sectionId); 
      const { id, createdAt, updatedAt, ...sectionData } = section;
      
      const duplicatedSection = {
        ...sectionData,
        title: `${sectionData.title} (Copy)`,
        status: 'draft',
        order: sections.length
      };

      await profileService.addProfileSection(bookId, duplicatedSection);
      await loadSections();
      alert('Section duplicated successfully!');
    } catch (error) {
      console.error('Error duplicating section:', error);
      alert('Failed to duplicate section');
    }
  };

  const handleToggleVisibility = async (sectionId) => {
    try {
      const section = sections.find(s => s.id === sectionId);
      if (section) {
        const currentVisibility = profileSettings.sectionVisibility?.[section.type] !== false;
        
        // Update profile settings
        const updatedSettings = {
          ...profileSettings,
          sectionVisibility: {
            ...profileSettings.sectionVisibility,
            [section.type]: !currentVisibility
          }
        };

        await profileService.updateProfileSettings(bookId, updatedSettings);
        await loadSections();
      }
    } catch (error) {
      console.error('Error toggling section visibility:', error);
      alert('Failed to update section visibility');
    }
  };

  const handleReorderSections = async (dragIndex, dropIndex) => {
    try {
      const updatedSections = [...sections];
      const draggedSection = updatedSections[dragIndex];
      
      updatedSections.splice(dragIndex, 1);
      updatedSections.splice(dropIndex, 0, draggedSection);
      
      // Update order values
      const reorderedSections = updatedSections.map((section, index) => ({
        ...section,
        order: index
      }));

      // Update in database - Fixed to include bookId and check for section.id
      await Promise.all(
        reorderedSections
          .filter(section => section.id) // Only update sections that have IDs
          .map(section =>
            profileService.updateProfileSection(bookId, section.id, { order: section.order })
          )
      );

      setSections(reorderedSections);
      await loadSections();
    } catch (error) {
      console.error('Error reordering sections:', error);
      alert('Failed to reorder sections');
    }
  };

  const handleBulkAction = async (action) => {
    const selectedIds = Array.from(bulkSelectedSections);
    
    if (selectedIds.length === 0) {
      alert('Please select sections first');
      return;
    }

    try {
      switch (action) {
        case 'delete':
          if (window.confirm(`Delete ${selectedIds.length} selected sections?`)) {
            await Promise.all(selectedIds.map(id => profileService.deleteProfileSection(bookId, id)));
            setBulkSelectedSections(new Set());
            await loadSections();
          }
          break;
        case 'show':
        case 'hide':
          const visibility = action === 'show';
          const sectionsToUpdate = sections.filter(s => selectedIds.includes(s.id));
          const updatedVisibility = { ...profileSettings.sectionVisibility };
          
          sectionsToUpdate.forEach(section => {
            updatedVisibility[section.type] = visibility;
          });

          await profileService.updateProfileSettings(bookId, {
            ...profileSettings,
            sectionVisibility: updatedVisibility
          });
          
          setBulkSelectedSections(new Set());
          await loadSections();
          break;
      }
    } catch (error) {
      console.error('Error performing bulk action:', error);
      alert('Failed to perform bulk action');
    }
  };

  const getSectionTypes = () => {
    const allTypes = [
      { value: 'hero', label: 'Hero Section', icon: User },
      { value: 'about', label: 'About', icon: User },
      { value: 'experience', label: 'Work Experience', icon: Briefcase },
      { value: 'education', label: 'Education', icon: Calendar },
      { value: 'skills', label: 'Skills', icon: Star },
      { value: 'projects', label: 'Projects', icon: Briefcase },
      { value: 'achievements', label: 'Achievements', icon: Trophy },
      { value: 'services', label: 'Services', icon: Package },
      { value: 'features', label: 'Features', icon: CheckCircle },
      { value: 'references', label: 'References', icon: Users },
      { value: 'gallery', label: 'Gallery', icon: ImageIcon },
      { value: 'testimonials', label: 'Testimonials', icon: MessageCircle },
      { value: 'stats', label: 'Statistics', icon: BarChart3 },
      { value: 'contact', label: 'Contact', icon: Mail }
    ];

    // Filter by profile type
    const typeFilters = {
      personal: ['hero', 'about', 'experience', 'education', 'skills', 'projects', 'achievements', 'references', 'gallery', 'contact', 'testimonials'],
      organization: ['hero', 'about', 'services', 'team', 'testimonials', 'stats', 'gallery', 'contact', 'testimonials'],
      project: ['hero', 'about', 'objectives', 'features', 'gallery', 'team', 'stats', 'contact', 'testimonials'],
      asset: ['hero', 'about', 'features', 'gallery', 'testimonials', 'contact']
    };

    const allowedTypes = typeFilters[profileType] || typeFilters.personal;
    return allTypes.filter(type => allowedTypes.includes(type.value));
  };

  const getSectionIcon = (type) => {
    const icons = {
      hero: User,
      about: User,
      objectives: CheckCircle,
      experience: Briefcase,
      education: Calendar,
      skills: Star,
      projects: Briefcase,
      achievements: Trophy,
      services: Package,
      features: CheckCircle,
      gallery: ImageIcon,
      testimonials: MessageCircle,
      references: Users,
      stats: BarChart3,
      contact: Mail
    };
    return icons[type] || User;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              {profileType === 'organization' && <Building className="h-7 w-7 text-purple-600" />}
              {profileType === 'project' && <Briefcase className="h-7 w-7 text-purple-600" />}
              {profileType === 'asset' && <Package className="h-7 w-7 text-purple-600" />}
              {profileType === 'personal' && <User className="h-7 w-7 text-purple-600" />}
              Profile Manager
            </h2>
            <p className="text-gray-600 capitalize">
              Managing {profileType} profile sections for {book?.botName}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowPreview(true)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 transition-colors"
            >
              <Eye className="h-4 w-4" />
              Preview Profile
            </button>
            <button
              onClick={() => setShowForm(true)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Add Section
            </button>
          </div>
        </div>

        {/* Section Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-purple-50 p-4 rounded-lg">
            <p className="text-2xl font-bold text-purple-600">{sections.length}</p>
            <p className="text-sm text-purple-700">Total Sections</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <p className="text-2xl font-bold text-green-600">
              {sections.filter(s => profileSettings.sectionVisibility?.[s.type] !== false).length}
            </p>
            <p className="text-sm text-green-700">Visible Sections</p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-2xl font-bold text-blue-600">
              {sections.filter(s => s.status === 'active').length}
            </p>
            <p className="text-sm text-blue-700">Active Sections</p>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg">
            <p className="text-2xl font-bold text-orange-600">
              {sectionStats.totalViews || 0}
            </p>
            <p className="text-sm text-orange-700">Total Views</p>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4 items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search sections..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>

          <div className="flex gap-2">
            <select
              value={filters.type}
              onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">All Types</option>
              {getSectionTypes().map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>

            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="hidden">Hidden</option>
            </select>

            <select
              value={filters.visibility}
              onChange={(e) => setFilters(prev => ({ ...prev, visibility: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">All Visibility</option>
              <option value="visible">Visible</option>
              <option value="hidden">Hidden</option>
            </select>

            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-purple-600 text-white' : 'bg-white text-gray-700'}`}
              >
                <Grid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-purple-600 text-white' : 'bg-white text-gray-700'}`}
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Bulk Actions */}
        {bulkSelectedSections.size > 0 && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm text-blue-700">
                {bulkSelectedSections.size} section{bulkSelectedSections.size !== 1 ? 's' : ''} selected
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => handleBulkAction('show')}
                  className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                >
                  Show
                </button>
                <button
                  onClick={() => handleBulkAction('hide')}
                  className="px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700"
                >
                  Hide
                </button>
                <button
                  onClick={() => handleBulkAction('delete')}
                  className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                >
                  Delete
                </button>
                <button
                  onClick={() => setBulkSelectedSections(new Set())}
                  className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Section Management */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {filteredSections.length === 0 ? (
          <div className="text-center py-12 px-6">
            <User className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm || filters.type !== 'all' ? 'No sections found' : 'No sections yet'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || filters.type !== 'all' 
                ? 'Try adjusting your search or filters'
                : 'Start building your profile by adding sections'
              }
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Add Your First Section
            </button>
          </div>
        ) : (
          <div className="p-6">
            {/* Section Order Guide */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Current Section Order</h4>
              <div className="flex flex-wrap gap-2">
                {profileSettings.sectionOrder?.map((sectionType, index) => {
                  const section = sections.find(s => s.type === sectionType);
                  const isVisible = profileSettings.sectionVisibility?.[sectionType] !== false;
                  const IconComponent = getSectionIcon(sectionType);
                  
                  return (
                    <div
                      key={sectionType}
                      className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                        isVisible 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      <span className="text-xs font-medium">{index + 1}</span>
                      <IconComponent className="h-3 w-3" />
                      <span className="capitalize">{sectionType}</span>
                      {!isVisible && <EyeOff className="h-3 w-3" />}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Sections Grid/List */}
            <div className={viewMode === 'grid' 
              ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" 
              : "space-y-4"
            }>
              {filteredSections.map((section, index) => (
                <ProfileSectionCard
                  key={section.id}
                  section={section}
                  profileType={profileType}
                  viewMode={viewMode}
                  showEditControls={true}
                  isVisible={profileSettings.sectionVisibility?.[section.type] !== false}
                  sectionStats={sectionStats[section.id]}
                  isSelected={bulkSelectedSections.has(section.id)}
                  onSelect={(selected) => {
                    const newSelection = new Set(bulkSelectedSections);
                    if (selected) {
                      newSelection.add(section.id);
                    } else {
                      newSelection.delete(section.id);
                    }
                    setBulkSelectedSections(newSelection);
                  }}
                  onEdit={() => {
                    setEditingSection(section);
                    setShowForm(true);
                  }}
                  onDelete={() => handleDeleteSection(section.id)}
                  onDuplicate={() => handleDuplicateSection(section.id)}
                  onToggleVisibility={() => handleToggleVisibility(section.id)}
                  onPreview={() => {
                    setPreviewSection(section);
                    setShowPreview(true);
                  }}
                  onMoveUp={() => index > 0 && handleReorderSections(index, index - 1)}
                  onMoveDown={() => index < filteredSections.length - 1 && handleReorderSections(index, index + 1)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Quick Add Sections */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Add Popular Sections</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {getSectionTypes()
            .filter(type => !sections.some(s => s.type === type.value))
            .slice(0, 8)
            .map(type => {
              const IconComponent = type.icon;
              return (
                <button
                  key={type.value}
                  onClick={() => {
                    setEditingSection({
                      type: type.value,
                      title: type.label,
                      status: 'active'
                    });
                    setShowForm(true);
                  }}
                  className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors"
                >
                  <IconComponent className="h-5 w-5 text-purple-600" />
                  <span className="text-sm font-medium text-gray-700">{type.label}</span>
                </button>
              );
            })}
        </div>
      </div>

      {/* Profile Section Form Modal */}
      {showForm && (
        <ProfileSectionForm
          section={editingSection}
          profileType={profileType}
          bookId={bookId}  
          onSave={handleSaveSection}
          onClose={() => {
            setShowForm(false);
            setEditingSection(null);
          }}
        />
      )}

      {/* Profile Preview Modal */}
      {showPreview && (
        <ProfilePreviewModal
          bookId={bookId}
          book={book}
          sections={sections}
          previewSection={previewSection}
          onClose={() => {
            setShowPreview(false);
            setPreviewSection(null);
          }}
        />
      )}

      {/* Testimonials Management */}
      <TestimonialsManagement 
        bookId={bookId}
        sections={sections}
        loadSections={loadSections}
      />
    </div>
  );
};

const TestimonialsManagement = ({ bookId, sections, loadSections }) => {
  const [testimonialSection, setTestimonialSection] = useState(null);
  const [expandedTestimonial, setExpandedTestimonial] = useState(null);

  useEffect(() => {
    const section = sections.find(s => s.type === 'testimonials');
    setTestimonialSection(section);
  }, [sections]);

  const handleToggleVisibility = async (testimonialIndex, currentVisibility) => {
    if (!testimonialSection) return;

    try {
      const updatedItems = [...testimonialSection.items];
      updatedItems[testimonialIndex] = {
        ...updatedItems[testimonialIndex],
        showOnWebsite: !currentVisibility
      };

      await profileService.updateProfileSection(bookId, testimonialSection.id, {
        ...testimonialSection,
        items: updatedItems
      });

      await loadSections();
    } catch (error) {
      console.error('Error updating testimonial visibility:', error);
    }
  };

  const handleDeleteTestimonial = async (testimonialIndex) => {
    if (!testimonialSection) return;

    if (window.confirm('Are you sure you want to delete this testimonial?')) {
      try {
        const updatedItems = testimonialSection.items.filter((_, index) => index !== testimonialIndex);

        await profileService.updateProfileSection(bookId, testimonialSection.id, {
          ...testimonialSection,
          items: updatedItems
        });

        await loadSections();
      } catch (error) {
        console.error('Error deleting testimonial:', error);
      }
    }
  };

  if (!testimonialSection || !testimonialSection.items || testimonialSection.items.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="text-center py-8">
          <MessageCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No testimonials yet
          </h3>
          <p className="text-gray-600">
            Testimonials submitted through your profile will appear here.
          </p>
        </div>
      </div>
    );
  }

  const publicTestimonials = testimonialSection.items.filter(t => t.showOnWebsite);
  const privateTestimonials = testimonialSection.items.filter(t => !t.showOnWebsite);
  const publicSubmissions = testimonialSection.items.filter(t => t.submittedByPublic);

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Testimonials Management
            </h3>
            <p className="text-sm text-gray-600">
              Manage client testimonials and control their visibility
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-900">
              {testimonialSection.items.length}
            </div>
            <div className="text-sm text-gray-600">Total</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-600">
              {publicTestimonials.length}
            </div>
            <div className="text-sm text-gray-600">Public</div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-yellow-600">
              {privateTestimonials.length}
            </div>
            <div className="text-sm text-gray-600">Private</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-600">
              {publicSubmissions.length}
            </div>
            <div className="text-sm text-gray-600">Submitted</div>
          </div>
        </div>
      </div>

      {/* Testimonials List */}
      <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
        {testimonialSection.items.map((testimonial, index) => (
          <div key={testimonial.id || index} className="p-6">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                  <User className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">
                    {testimonial.clientName}
                  </h4>
                  {(testimonial.clientTitle || testimonial.clientCompany) && (
                    <p className="text-sm text-gray-600">
                      {testimonial.clientTitle}
                      {testimonial.clientTitle && testimonial.clientCompany && ' at '}
                      {testimonial.clientCompany}
                    </p>
                  )}
                  <div className="flex items-center gap-4 mt-2">
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star 
                          key={star}
                          className={`h-3 w-3 ${
                            star <= (testimonial.rating || 5)
                              ? 'text-yellow-400 fill-current' 
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    {testimonial.date && (
                      <span className="text-xs text-gray-500">
                        <Calendar className="h-3 w-3 inline mr-1" />
                        {new Date(testimonial.date).toLocaleDateString()}
                      </span>
                    )}
                    {testimonial.submittedByPublic && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Public Submission
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleToggleVisibility(index, testimonial.showOnWebsite)}
                  className={`
                    p-2 rounded-lg transition-colors
                    ${testimonial.showOnWebsite 
                      ? 'text-green-600 hover:bg-green-50' 
                      : 'text-gray-400 hover:bg-gray-50'
                    }
                  `}
                  title={testimonial.showOnWebsite ? 'Hide from public' : 'Make public'}
                >
                  {testimonial.showOnWebsite ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </button>
                <button
                  onClick={() => handleDeleteTestimonial(index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete testimonial"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Testimonial Text */}
            <div className="mb-4">
              <p className="text-gray-700 text-sm leading-relaxed">
                {expandedTestimonial === index || testimonial.testimonial.length <= 200
                  ? testimonial.testimonial
                  : `${testimonial.testimonial.substring(0, 200)}...`
                }
              </p>
              
              {testimonial.testimonial.length > 200 && (
                <button
                  onClick={() => setExpandedTestimonial(
                    expandedTestimonial === index ? null : index
                  )}
                  className="text-blue-600 hover:text-blue-700 text-sm mt-2"
                >
                  {expandedTestimonial === index ? 'Show less' : 'Read more'}
                </button>
              )}
            </div>

            {/* Additional Info */}
            <div className="flex items-center gap-4 text-xs text-gray-500">
              {testimonial.projectTitle && (
                <div className="flex items-center gap-1">
                  <Building className="h-3 w-3" />
                  Project: {testimonial.projectTitle}
                </div>
              )}
              {testimonial.serviceCategory && (
                <div className="flex items-center gap-1">
                  <MessageCircle className="h-3 w-3" />
                  {testimonial.serviceCategory}
                </div>
              )}
              {testimonial.contactPermission && (
                <div className="flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  Contact OK
                </div>
              )}
            </div>

            {/* Status */}
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className={`
                  inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                  ${testimonial.showOnWebsite 
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                  }
                `}>
                  {testimonial.showOnWebsite ? 'Public' : 'Private'}
                </span>
              </div>
              
              <div className="text-xs text-gray-500">
                Click the eye icon to {testimonial.showOnWebsite ? 'hide' : 'show'} publicly
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Profile Preview Modal Component
const ProfilePreviewModal = ({ bookId, book, sections, previewSection, onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
      <div className="bg-purple-600 text-white p-4 flex items-center justify-between">
        <h3 className="text-lg font-semibold">
          {previewSection ? `Preview: ${previewSection.title}` : 'Profile Preview'}
        </h3>
        <button
          onClick={onClose}
          className="p-2 hover:bg-purple-700 rounded-lg transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      
      <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
        <ProfileSectionPreview 
          bookId={bookId}
          book={book}
          sections={sections}
          highlightSection={previewSection}
        />
      </div>
    </div>
  </div>
);